<template>
  <action-dispatcher action="product/getProduct" :parameters="$route.params.id">
    <h2>{{ editing ? "Editar oferta" : "Nova oferta" }}</h2>

    <div class="mb-6 mt-7">
      <h3>Informações básicas</h3>
      <span class="text-neutral-400 fs-14">
        Através dessas informações seu cliente vai identificar essa oferta.
      </span>
    </div>

    <bp-input
      v-model="form.name"
      id="name"
      label="Nome da oferta"
      placeholder="Ex: Promoção de Natal"
      description="Este nome será exibido apenas para você, em sua tabela de preços."
      :limit="250"
      required
      :errors="errors.name"
    />

    <bp-input
      v-model="form.description"
      id="description"
      textarea
      label="Descrição no checkout"
      placeholder="Insira uma descrição para sua página de checkout"
      description="Atenção! Esta é a descrição que exibiremos para os seus compradores."
      class="mt-6"
      size="xl"
      rows="5"
      :limit="980"
      required
      :errors="errors.description"
    />

    <bp-input
      v-model="form.sck"
      id="sck"
      label="SCK"
      placeholder="Digite o parâmetro desejado"
      description="Esse parâmetro te ajudará a identificar a origem da compra."
      class="mt-6"
    />

    <b-form-checkbox
      v-model="form.is_stock"
      switch
      class="mt-6"
      :class="{ 'text-primary': form.is_stock }"
    >
      Ativar/Desativar estoque
    </b-form-checkbox>

    <div v-if="form.is_stock">
      <div class="row">
        <div class="col-md-6">
          <bp-input
            v-model="form.stock"
            id="stock"
            number
            label="Quantidade em estoque"
            placeholder="0"
            :description="`Vendas realizadas do estoque: ${form.stock_sales}`"
            class="mt-6"
            :min="1"
            mask="##########"
            required
            :errors="errors.stock"
          />
        </div>

        <div class="col-md-6">
          <bp-input
            v-model="form.stock_empty_url"
            id="stock_empty_url"
            label="URL para redirecionar após estoque acabar"
            placeholder="Ex: http://www.meusite.com.br/estoque-acabou"
            class="mt-6"
            :errors="errors.stock_empty_url"
          />
        </div>
      </div>
    </div>

    <hr class="my-6" />

    <div class="mb-5">
      <h3>Período</h3>
      <span class="text-neutral-400 fs-14">Defina o período da oferta</span>
    </div>

    <div class="row">
      <div class="col-lg-6">
        <form-group
          v-model="form.start_at"
          id="start_at"
          is-date
          label="Início da oferta"
          placeholder="Selecione a data incial..."
          readonly
          mode="dateTime"
          withClearButton
          :minDate="new Date()"
          required
          :errors="errors.start_at"
        />
        <span class="text-secondary fs-14">
          Escolha a data a partir da qual a oferta ficará disponível
        </span>
      </div>

      <div class="col-lg-6">
        <form-group
          v-model="form.end_at"
          id="end_at"
          is-date
          label="Fim da oferta"
          placeholder="Selecione a data final..."
          readonly
          mode="dateTime"
          withClearButton
          :disabled="!form.start_at"
          :minDate="minEndDate"
          :errors="errors.end_at"
        />
        <span class="text-secondary fs-14">
          Escolha a data a partir da qual a oferta ficará indisponível
        </span>
      </div>
    </div>

    <bp-input
      v-model="form.expire_url"
      id="expire_url"
      label="URL para redirecionar após oferta expirar"
      placeholder="Ex: http://www.meusite.com.br/oferta-expirou"
      class="mt-6"
      :errors="errors.expire_url"
    />

    <hr class="my-6" />

    <div class="mb-6">
      <h3>Precificação</h3>
      <span class="text-neutral-400 fs-14">
        Informe como você deseja monetizar esta oferta
      </span>
    </div>

    <bp-input
      v-model="form.price"
      money
      required
      class="my-5"
      placeholder="Informe o preço"
      label="Valor em Real Brasileiro (R$)"
      preIcon="fa-light fa-brazilian-real-sign"
      description="Defina quanto custará o seu produto. Certifique-se de que o valor não seja menor que R$ 4,00."
      :min="4"
      :errors="errors.price"
      :moneyConfig="moneyConfig"
    />
    <bp-input
      v-if="!isParcelledBillet"
      :disabled="!showForeign"
      :info="getInfo('USD')"
      v-model="form.price_usd"
      money
      class="my-5"
      preIcon="fa-light fa-dollar"
      placeholder="Informe o preço"
      label="Valor em Dólar Americano (US$)"
      description="Defina quanto custará o seu produto. Certifique-se de que o valor não seja menor que US$ 4,00."
      :min="4"
      :errors="errors.price_usd"
      :moneyConfig="{ ...moneyConfig, currency: '$' }"
    />
    <bp-input
      v-if="!isParcelledBillet"
      :disabled="!showForeign"
      :info="getInfo('EUR')"
      v-model="form.price_eur"
      money
      class="my-5"
      label="Valor em Euro (€)"
      preIcon="fa-light fa-euro"
      placeholder="Informe o preço"
      description="Defina quanto custará o seu produto. Certifique-se de que o valor não seja menor que € 4,00."
      :min="4"
      :errors="errors.price_eur"
      :moneyConfig="{ ...moneyConfig, currency: '€' }"
    />

    <hr class="my-6" />

    <h3>Configurar Order Bump</h3>
    <span class="text-neutral-400 fs-14">
      Adicione um ou mais Order Bump dentro desta oferta
    </span>

    <action-dispatcher
      action="product/get"
      :parameters="{ limit: 1000 }"
      class="mt-5"
    >
      <order-bump-add
        v-for="(order_bump, i) in getListedOrderBumps()"
        class="mb-8"
        :counter="i + 1"
        :form="order_bump"
        :key="order_bump.index"
        :product-id="$route.params.id"
        @remove="removeItem(order_bump, order_bump.index)"
      />
    </action-dispatcher>
    <bp-button
      label="Novo Order Bump"
      variant="outlined"
      class="text-primary-700"
      icon="fa-light fa-plus"
      @click="form.order_bumps.push({})"
    />

    <div class="d-flex flex-column flex-md-row justify-content-between my-9">
      <bp-button
        label="Cancelar"
        color="neutral"
        class="order-1 order-md-0"
        variant="filled-1"
        @click="
          $router.push({
            name: 'product-prices',
            params: { id: $route.params.id }
          })
        "
      />
      <bp-button
        label="Salvar Oferta"
        :loading="loadingSubmit"
        :disabled="!isValid"
        :color="isValid ? 'primary' : 'neutral'"
        @click="submit"
      />
      <div class="d-md-none my-3" />
    </div>
  </action-dispatcher>
</template>

<script>
import OrderBumpAdd from "@/components/product/order-bump/OrderBumpAdd";
import {
  formatMoney,
  formFields,
  toComputerDate,
  toHumanDate
} from "@/functions/helpers";
import moment from "moment";
import { mapState } from "vuex";

export default {
  components: { OrderBumpAdd },
  data() {
    return {
      loading: false,
      isActive: true,
      loadingSubmit: false,
      ...formFields(
        [
          "id",
          "name",
          "description",
          "price",
          "price_usd",
          "price_eur",
          "currency",
          "installments",
          "is_main",
          "status",
          "start_at",
          "end_at",
          "order_bump",
          "order_bumps",
          "sck",
          "expire_url",
          "is_stock",
          "stock",
          "stock_empty_url"
        ],
        {
          status: "active",
          order_bumps: [{}],
          installments: 1,
          is_stock: false,
          currency: "BRL",
          is_main: false
        }
      ),
      moneyConfig: {
        decimal: ",",
        thousands: ".",
        precision: 2,
        prefix: "",
        masked: false
      }
    };
  },
  computed: {
    ...mapState("product", ["product", "products"]),
    ...mapState("offer", ["offer", "orderBumpOffers"]),
    ...mapState("sale", ["installments"]),
    minEndDate() {
      return moment(this.form.start_at, "DD/MM/YYYY hh:mm").toDate();
    },
    isValid() {
      return (
        this.form.name &&
        this.form.description &&
        this.form.description.length <= 980 &&
        (!this.form.is_stock || this.form.stock > 0) &&
        this.form.start_at &&
        this.form.currency &&
        this.form.price &&
        this.form.price >= 4 &&
        (!this.hasUSD || this.form.price_usd >= 4) &&
        (!this.hasEUR || this.form.price_eur >= 4) &&
        !this.form.order_bumps.some(
          order_bump => !!order_bump.product_id && !order_bump.offer_id
        )
      );
    },
    installmentOptions() {
      const opts = [];
      for (let id in this.installments) {
        opts.push({
          id: parseInt(id),
          name: `Até ${id} parcela${id - 1 ? "s" : ""} de R$ ${formatMoney(
            this.installments[id]
          )}`
        });
      }
      return opts;
    },
    editing() {
      return !!this.$route.params.offer_id;
    },
    showForeign() {
      return this.product.payment_methods?.some(({ id }) => id === 1);
    },
    hasUSD() {
      return !!this.form.price_usd && this.form.price_usd > 0;
    },
    hasEUR() {
      return !!this.form.price_eur && this.form.price_eur > 0;
    },
    isParcelledBillet() {
      return this.product?.billet_installments > 1;
    }
  },
  methods: {
    getInfo(currency) {
      if (this.showForeign) return "";
      const currencyName = {
        USD: "dólar americano",
        EUR: "euro"
      };
      return `Para habilitar configuração de preço em ${currencyName[currency]} seu checkout precisa aceitar Cartão de Crédito. Atualize a configuração de "Opções de Pagamento" do seu produto em "Configurar Checkout"`;
    },
    getListedOrderBumps() {
      const bumps = [];
      for (let i in this.form.order_bumps) {
        this.form.order_bumps[i].index = i;
        if (!this.form.order_bumps[i].deleted)
          bumps.push(this.form.order_bumps[i]);
      }
      return bumps;
    },
    submit() {
      if (this.loadingSubmit) return;

      if (!this.form.start_at) {
        this.$message.warning(
          "É necessário escolher uma data válida de início antes de salvar."
        );
        return;
      }

      this.loadingSubmit = true;

      const data = {
        ...this.form,
        product_id: this.$route.params.id,
        end_at: toComputerDate(this.form.end_at),
        start_at: toComputerDate(this.form.start_at),
        status: this.isActive ? "active" : "inactive"
      };

      if (data.order_bumps) {
        data.order_bumps = data.order_bumps.filter(offer => offer.offer_id);
      }

      data.price_eur = parseFloat(data.price_eur) === 0 ? "" : data.price_eur;
      data.price_usd = parseFloat(data.price_usd) === 0 ? "" : data.price_usd;

      const action = this.$route.params.offer_id ? "offer/put" : "offer/post";

      this.$store
        .dispatch(action, data)
        .then(() => {
          this.$message.success("Oferta salva com sucesso");
          this.$router.push({
            name: "product-prices",
            params: { id: this.$route.params.id }
          });
        })
        .catch(error => {
          this.errors = error.response.data;
          this.$message.error("Erro ao salvar oferta");
        })
        .finally(() => (this.loadingSubmit = false));
    },
    removeItem(item, index) {
      if (item.code) {
        this.form.order_bumps[index] = { ...item, deleted: true };
        this.$forceUpdate();
      } else {
        this.form.order_bumps.splice(index, 1);
      }
    }
  },
  mounted() {
    if (this.$route.params.offer_id) {
      this.loading = true;
      this.$store
        .dispatch("offer/getOffer", this.$route.params.offer_id)
        .then(data => {
          this.form = {
            ...this.form,
            ...data,
            start_at: toHumanDate(data.start_at),
            end_at: toHumanDate(data.end_at),
            order_bumps: data.order_bumps?.map(item => ({
              product_id: item.offer?.product,
              offer_id: item.offer?.id,
              code: item.code
            })),
            price_usd: data.price_usd || "",
            price_eur: data.price_eur || ""
          };
          this.isActive = data.status === "active";
        })
        .finally(() => (this.loading = false));
    }
  },
  watch: {
    "form.start_at"(value) {
      if (
        this.form.end_at &&
        moment(this.form.end_at, "DD/MM/YYYY hh:mm").isBefore(
          moment(value, "DD/MM/YYYY hh:mm")
        )
      ) {
        this.form.end_at = "";
      }
    }
  }
};
</script>
